import {
  ChevronLeft,
  LayoutDashboard,
  LogOut,
  Plus,
  Settings,
  Users2,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { CreatePaymentLinkDialog } from "./create-payment-link-dialog";
import { Dialog, DialogTrigger } from "./ui/dialog";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

interface SidebarItemProps {
  icon: React.ReactNode;
  text: string;
  active?: boolean;
  collapsed: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

function SidebarItem({
  icon,
  text,
  active,
  collapsed,
  onClick,
}: SidebarItemProps) {
  return (
    <Button
      onClick={onClick}
      variant="ghost"
      className={cn(
        "w-full gap-3 text-primary text-sm",
        active
          ? "border-2 border-primary hover:bg-primary/5"
          : `bg-primary/5 hover:bg-primary/10`,
        collapsed ? "px-0 justify-center" : "px-4 justify-start"
      )}
    >
      {icon}
      {!collapsed && <span>{text}</span>}
    </Button>
  );
}

export function Sidebar() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [collapsed, setCollapsed] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <aside
      className={cn(
        "border-r h-full transition-all duration-300 flex flex-col sticky top-0 left-0 bg-card",
        collapsed ? "w-[80px]" : "w-[280px]"
      )}
    >
      <div className="flex flex-col flex-1 p-4 gap-2">
        <Button
          variant="ghost"
          className={cn(
            "mb-4 text-primary w-fit h-12",
            collapsed ? "self-center" : "self-end"
          )}
          onClick={() => setCollapsed(!collapsed)}
        >
          <ChevronLeft
            className={cn(
              "h-4 w-4 transition-transform",
              collapsed && "rotate-180"
            )}
          />
        </Button>

        <SidebarItem
          icon={<LayoutDashboard className="h-4 w-4" />}
          text="Dashboard"
          active={location.pathname === "/dashboard"}
          collapsed={collapsed}
          onClick={() => navigate("/dashboard")}
        />

        {user?.org === "PAGAR" && (
          <div className="mt-2">
            <p
              className={cn(
                "text-xs text-gray-500 font-medium mb-2",
                collapsed && "px-3"
              )}
            >
              {!collapsed && "Configurações"}
            </p>
            <div className="space-y-2">
              <SidebarItem
                icon={<Settings className="h-4 w-4" />}
                text="Geral"
                active={location.pathname === "/settings"}
                collapsed={collapsed}
                onClick={() => navigate("/settings")}
              />
              <SidebarItem
                icon={<Users2 className="h-4 w-4" />}
                text="Usuários"
                active={location.pathname.includes("/users")}
                collapsed={collapsed}
                onClick={() => navigate("/users")}
              />
            </div>
          </div>
        )}
        {user?.org === "PAGAR" && <Separator className="my-4" />}
        {user?.org === "PAGAR" && (
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger>
              <SidebarItem
                icon={<Plus className="h-4 w-4" />}
                text="Link de Pagamento"
                collapsed={collapsed}
              />
            </DialogTrigger>

            {isOpen && <CreatePaymentLinkDialog setIsOpen={setIsOpen} />}
          </Dialog>
        )}

        <Separator className="my-4" />

        <div
          className={cn("rounded-2xl", {
            "bg-white-100": user?.org !== "SANEPAR",
            "bg-blue-500": user?.org === "SANEPAR",
          })}
        >
          <div className="flex w-full justify-center rounded-tl-2xl rounded-tr-2xl my-4 py-1">
            <img
              src={`/images/${user?.org?.toLowerCase() || "pagar"}.png`}
              alt={user?.org || "Pagar" + " Logo"}
              className={cn("w-fit", collapsed ? "h-3" : "h-5")}
            />
          </div>
          {!collapsed && (
            <div className="bg-white-100 text-primary rounded-bl-xl rounded-br-xl">
              <div className="flex flex-col p-4 bg-primary/10 rounded-bl-xl rounded-br-xl">
                <p className="font-medium text-sm">{user?.name}</p>
                <p className="text-xs">{user?.email}</p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-1">
          {/* <SidebarItem
            icon={<HelpCircle className="h-4 w-4" />}
            text="Ajuda"
            collapsed={collapsed}
          />
          <SidebarItem
            icon={<PhoneCall className="h-4 w-4" />}
            text="Abrir chamado"
            collapsed={collapsed}
          /> */}
          <SidebarItem
            icon={<LogOut className="h-4 w-4" />}
            text="Sair"
            collapsed={collapsed}
            onClick={() => {
              logout();
              navigate("/login");
            }}
          />
        </div>
      </div>
    </aside>
  );
}
